import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { FiChevronDown, FiX } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { soleOptionIdFromList, useAutoSelectSingleOption } from '../../hooks/useAutoSelectSingleOption'
import { isSameEntityId } from '../../utils/entityId'
import DropdownSearchHeader from './DropdownSearchHeader'
import { useLookupRefresh } from '../../hooks/useLookupRefresh'
import { useDropdownFieldConfig } from '../../hooks/useDropdownFieldConfig'
import { formatConfiguredLabel, formatConfiguredParts } from '../../utils/dropdownFieldConfig'
import { mapPagedResponse, useInfiniteLookup } from '../../hooks/useInfiniteLookup'

interface Account {
  id: string
  name: string
  nameEn?: string
  displayOrder?: number
  cachedNumber?: string
  isActive?: boolean
  accountType?: string
}

interface AccountDropdownProps {
  value?: string
  onChange: (accountId: string | undefined) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** `leaf` = terminal accounts only (journal/vouchers). `all` = full chart incl. parent groups (company constants). */
  accountMode?: 'leaf' | 'all'
  autoSelectSingle?: boolean
  /** `id` on the trigger button (for programmatic focus from parent forms). */
  triggerId?: string
  /** When the list is closed and the user presses Enter or Tab on the trigger, move focus instead of submitting the form. */
  onEnterWhenClosed?: () => void
  /** Fired after the user picks an account from the list (not when clearing). */
  onAccountSelected?: (account: { id: string; accountType?: string }) => void
  /** When this number changes (e.g. timestamp from parent), opens the dropdown and focuses search. */
  autoOpenNonce?: number
}

function mapAccountRow(account: any): Account | null {
  const mapped = {
    id: String(account.id || account.ID || '').trim(),
    name: String(account.name || account.Name || '').trim(),
    nameEn: account.nameEn || account.NameEN || account.NameEn || '',
    displayOrder: account.displayOrder ?? account.DisplayOrder ?? 0,
    isActive: account.isActive ?? account.IsActive ?? true,
    cachedNumber: account.cachedNumber || account.CachedNumber || '',
    accountType: String(account.accountType ?? account.AccountType ?? '').trim() || undefined,
  }
  if (!mapped.id || !mapped.name) return null
  if (mapped.isActive === false || mapped.isActive === 0 || mapped.isActive === 'false') return null
  return mapped
}

/** Legacy /leaf or /list with client-side page + search (used when /lookup is unavailable or empty). */
async function fetchLegacyAccountsPage(args: {
  page: number
  pageSize: number
  search: string
  leafOnly: boolean
}): Promise<{ items: Account[]; hasNextPage: boolean; totalCount?: number }> {
  const endpoint = args.leafOnly
    ? API_ENDPOINTS.chartOfAccounts.leaf
    : API_ENDPOINTS.chartOfAccounts.list
  const response = await apiClient.get(endpoint)
  const raw = response.data
  const rawList: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
  let all = rawList.map(mapAccountRow).filter((x): x is Account => x != null)

  const search = args.search.trim().toLowerCase()
  if (search) {
    all = all.filter((a) => {
      const name = (a.name || '').toLowerCase()
      const nameEn = (a.nameEn || '').toLowerCase()
      const code = (a.cachedNumber || '').toLowerCase()
      const id = (a.id || '').toLowerCase()
      return name.includes(search) || nameEn.includes(search) || code.includes(search) || id.includes(search)
    })
  }

  all.sort((a, b) => {
    const orderDiff = (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    if (orderDiff !== 0) return orderDiff
    return (a.name || '').localeCompare(b.name || '')
  })

  const start = (args.page - 1) * args.pageSize
  const items = all.slice(start, start + args.pageSize)
  return {
    items,
    hasNextPage: start + args.pageSize < all.length,
    totalCount: all.length,
  }
}

export default function AccountDropdown({
  value,
  onChange,
  placeholder,
  className = '',
  disabled = false,
  accountMode = 'leaf',
  autoSelectSingle = true,
  triggerId,
  onEnterWhenClosed,
  onAccountSelected,
  autoOpenNonce = 0,
}: AccountDropdownProps) {
  const { language } = useLanguageStore()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAbove, setShowAbove] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownMenuRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const fieldConfig = useDropdownFieldConfig('account')
  const getId = useCallback((a: Account) => String(a.id || ''), [])
  const leafOnly = accountMode === 'leaf'
  const useLegacyLookupRef = useRef(false)

  const {
    items: accounts,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    sentinelRef,
  } = useInfiniteLookup<Account>({
    queryKey: ['chartOfAccounts', 'lookup', accountMode],
    enabled: !disabled && (isOpen || !value || autoSelectSingle),
    pageSize: 40,
    searchTerm,
    searchFields: fieldConfig.searchFields,
    getId,
    scrollRootRef: listRef,
    fetchPage: async ({ page, pageSize, search, searchFields }) => {
      if (page === 1 && !search) {
        useLegacyLookupRef.current = false
      }
      if (useLegacyLookupRef.current) {
        return fetchLegacyAccountsPage({ page, pageSize, search, leafOnly })
      }
      try {
        const response = await apiClient.get(API_ENDPOINTS.chartOfAccounts.lookup, {
          params: {
            page,
            pageSize,
            searchTerm: search || undefined,
            searchFields: searchFields.length > 0 ? searchFields.join(',') : undefined,
            leafOnly,
          },
        })
        const mapped = mapPagedResponse(response.data, mapAccountRow, page, pageSize)
        // Empty first page with no search → fall back to legacy endpoints (older API / shared COA).
        if (page === 1 && !search && mapped.items.length === 0) {
          useLegacyLookupRef.current = true
          return fetchLegacyAccountsPage({ page, pageSize, search, leafOnly })
        }
        return mapped
      } catch {
        useLegacyLookupRef.current = true
        return fetchLegacyAccountsPage({ page, pageSize, search, leafOnly })
      }
    },
  })

  const { data: selectedById } = useQuery({
    queryKey: ['chartOfAccounts', 'selected', value],
    enabled: !!value && !accounts.some((a) => isSameEntityId(a.id, value)),
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.chartOfAccounts.get(String(value)))
      return mapAccountRow(response.data)
    },
  })

  const filteredAccounts = useMemo(() => {
    if (!selectedById?.id) return accounts
    if (accounts.some((a) => isSameEntityId(a.id, selectedById.id))) return accounts
    return [selectedById, ...accounts]
  }, [accounts, selectedById])

  const filteredAccountsRef = useRef(filteredAccounts)
  useEffect(() => {
    filteredAccountsRef.current = filteredAccounts
  }, [filteredAccounts])

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchTerm])

  useEffect(() => {
    if (disabled) return
    if (!autoOpenNonce) return
    setIsOpen(true)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }, [autoOpenNonce, disabled])

  const selectedAccount =
    filteredAccounts.find((account: Account) => isSameEntityId(account.id, value)) ||
    selectedById ||
    undefined

  const soleAccountIdStr = soleOptionIdFromList(accounts, (a) => a.id)

  const applySoleAccount = useCallback(() => {
    const id = accounts[0]?.id
    if (!id) return
    onChange(String(id))
  }, [accounts, onChange])

  const { markUserCleared } = useAutoSelectSingleOption({
    soleIdStr: soleAccountIdStr,
    currentValue: value,
    onApplySole: applySoleAccount,
    isLoading,
    disabled,
    autoSelectSingle,
  })

  const { onRefresh, isRefreshing } = useLookupRefresh({
    refetch,
    isFetching,
    items: accounts,
    selectedId: value,
    onClearSelection: () => onChange(undefined),
    language,
  })

  useEffect(() => {
    if (!isOpen || disabled) return

    const updatePosition = () => {
      if (!dropdownRef.current) return
      const rect = dropdownRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const dropdownHeight = 256

      const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight
      setShowAbove(shouldShowAbove)
      setDropdownPosition({
        top: shouldShowAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 280),
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, disabled, language])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current && !dropdownRef.current.contains(target) &&
          dropdownMenuRef.current && !dropdownMenuRef.current.contains(target)) {
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
      }
    }

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const handleSelect = useCallback((account: Account) => {
    onChange(account.id)
    onAccountSelected?.({ id: account.id, accountType: account.accountType })
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }, [onChange, onAccountSelected])

  const handleSelectRef = useRef(handleSelect)
  useEffect(() => {
    handleSelectRef.current = handleSelect
  }, [handleSelect])

  const highlightedIndexRef = useRef(highlightedIndex)
  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex
  }, [highlightedIndex])

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1)
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const t = event.target as Node | null
      if (
        t &&
        !dropdownRef.current?.contains(t) &&
        !dropdownMenuRef.current?.contains(t)
      ) {
        return
      }

      const list = filteredAccountsRef.current
      const totalItems = list.length

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          if (totalItems === 0) return
          setHighlightedIndex((prev) => {
            const newIndex = prev < totalItems - 1 ? prev + 1 : prev
            requestAnimationFrame(() => {
              itemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            })
            return newIndex
          })
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          if (totalItems === 0) return
          setHighlightedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : 0
            requestAnimationFrame(() => {
              itemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            })
            return newIndex
          })
          break
        }
        case 'Enter': {
          event.preventDefault()
          const idx = highlightedIndexRef.current
          if (idx >= 0 && idx < totalItems) {
            const account = list[idx]
            if (account) handleSelectRef.current(account)
          }
          break
        }
        case 'Escape': {
          event.preventDefault()
          setIsOpen(false)
          setSearchTerm('')
          setHighlightedIndex(-1)
          break
        }
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    markUserCleared()
    onChange(undefined)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }

  const dropdownMenu = !disabled && isOpen ? (
    <div
      ref={dropdownMenuRef}
      className="fixed z-[100060] bg-white border border-gray-200 rounded-lg shadow-2xl max-h-64 overflow-hidden"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
      }}
    >
      <DropdownSearchHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        isRTL={language === 'ar'}
      />

      <div ref={listRef} className="overflow-y-auto max-h-48">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : isError && filteredAccounts.length === 0 ? (
          <div className="p-4 text-center text-red-500 text-sm">
            {language === 'ar' ? 'تعذر تحميل الحسابات' : 'Failed to load accounts'}
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {searchTerm
              ? (language === 'ar' ? 'لا توجد حسابات مطابقة للبحث' : 'No accounts match your search')
              : (language === 'ar' ? 'لا توجد حسابات' : 'No accounts found')}
          </div>
        ) : (
          <ul className="py-1">
            {filteredAccounts.map((account: Account, index: number) => {
              const isSelected = isSameEntityId(account.id, value)
              const isHighlighted = highlightedIndex === index
              const parts = formatConfiguredParts(account, 'account', fieldConfig.listFields, language)
              const label = parts.secondary.length > 0
                ? `${parts.primary} - ${parts.secondary.join(' · ')}`
                : parts.primary

              return (
                <li key={account.id}>
                  <button
                    type="button"
                    ref={(el) => {
                      itemRefs.current[index] = el
                    }}
                    onClick={() => handleSelect(account)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-4 py-2.5 text-sm transition-colors ${
                      language === 'ar' ? 'text-right' : 'text-left'
                    } ${
                      isHighlighted
                        ? 'bg-primary-100 text-gray-900'
                        : isSelected
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                </li>
              )
            })}
            <li>
              <div ref={sentinelRef} className="h-6 flex items-center justify-center text-xs text-gray-400">
                {isFetchingNextPage
                  ? language === 'ar'
                    ? 'جاري تحميل المزيد…'
                    : 'Loading more…'
                  : null}
              </div>
            </li>
          </ul>
        )}
      </div>
    </div>
  ) : null

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      <button
        type="button"
        id={triggerId}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) {
            setIsOpen((prev) => !prev)
          }
        }}
        onKeyDown={(e) => {
          if (disabled || isOpen) return
          if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault()
            onEnterWhenClosed?.()
          }
        }}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-between gap-2 ${
          disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white border-gray-300 hover:border-gray-400'
        } ${language === 'ar' ? 'text-right flex-row-reverse' : 'text-left'}`}
      >
        <span className={`flex-1 truncate ${selectedAccount ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedAccount
            ? formatConfiguredLabel(selectedAccount, 'account', fieldConfig.triggerFields, language)
            : placeholder || (language === 'ar' ? 'اختر الحساب' : 'Select account')}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selectedAccount && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            >
              <FiX className="w-4 h-4" />
            </span>
          )}
          <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {dropdownMenu && createPortal(dropdownMenu, document.body)}
    </div>
  )
}
